import React, { useState, useEffect } from 'react';
import { getLead, movLeadStage, getLeadMoves, moveLeadToNode, addLeadActivity, setLeadFollowUp, updateLead, assignLead, getUsers } from '../api/leadsApi';
import { useAuth } from '../context/AuthContext';
import LeadForm from './LeadForm';
import UpworkBadge from './UpworkBadge';

const STAGE_FLOW = [
  { key: 'prospect', label: 'Prospect', color: '#888' },
  { key: 'contacted', label: 'Contacted', color: '#185FA5' },
  { key: 'qualified', label: 'Qualified', color: '#3B6D11' },
  { key: 'discovery_scheduled', label: 'Discovery Call', color: '#BA7517' },
  { key: 'proposal_sent', label: 'Proposal Sent', color: '#534AB7' },
  { key: 'negotiation', label: 'Negotiation', color: '#993C1D' },
];

const ALL_STAGES = {
  prospect:             { label: 'Prospect',       color: '#888' },
  contacted:            { label: 'Contacted',       color: '#185FA5' },
  qualified:            { label: 'Qualified',       color: '#3B6D11' },
  discovery_scheduled:  { label: 'Discovery Call',  color: '#BA7517' },
  proposal_sent:        { label: 'Proposal Sent',   color: '#534AB7' },
  negotiation:          { label: 'Negotiation',     color: '#993C1D' },
  closed_won:           { label: '✓ Closed Won',    color: '#0F6E56' },
  retainer:             { label: '⭐ Retainer',      color: '#BA7517' },
  closed_lost:          { label: 'Closed Lost',     color: '#A32D2D' },
  re_nurture:           { label: '♻ Re-nurture',    color: '#534AB7' },
};

// Must mirror backend STAGE_TRANSITIONS in leads.service.ts
const STAGE_TRANSITIONS = {
  prospect:            ['contacted', 're_nurture'],
  contacted:           ['qualified', 're_nurture'],
  qualified:           ['discovery_scheduled', 'proposal_sent', 'negotiation', 're_nurture'],
  discovery_scheduled: ['proposal_sent', 'negotiation', 're_nurture'],
  proposal_sent:       ['discovery_scheduled', 'negotiation', 'closed_won', 'closed_lost', 're_nurture'],
  negotiation:         ['closed_won', 'closed_lost', 're_nurture'],
  closed_won:          ['retainer'],
  closed_lost:         ['re_nurture'],
  retainer:            [],
  re_nurture:          ['prospect', 'contacted'],
};

const SOURCE_LABELS = { upwork: 'Upwork', client_referral: 'Client Referral', personal_referral: 'Personal Referral', linkedin: 'LinkedIn', inbound: 'Inbound', other: 'Other' };
const USE_CASE_LABELS = { workflow_automation: 'Workflow Automation', autonomous_agents: 'Autonomous AI Agents', chatbot: 'Chatbot', data_processing: 'Data Processing', customer_service: 'Customer Service', sales_automation: 'Sales Automation', custom: 'Custom' };
const DEAL_LABELS = { small: '< $5k', medium: '$5k–$20k', large: '$20k–$50k', enterprise: '$50k+' };
const TIMELINE_LABELS = { immediate: '< 2 weeks', short: '2–4 weeks', medium: '1–3 months', long: '3+ months', unknown: 'Unknown' };

export default function LeadDetail({ leadId, onClose }) {
  const { isSuperAdmin } = useAuth();
  const [lead, setLead] = useState(null);
  const [tab, setTab] = useState('overview');
  const [users, setUsers] = useState([]);
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [activityNote, setActivityNote] = useState('');
  const [activityAction, setActivityAction] = useState('Call made');
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpNote, setFollowUpNote] = useState('');
  const [stageNote, setStageNote] = useState('');
  const [lostReason, setLostReason] = useState('');
  const [reNurtureDate, setReNurtureDate] = useState('');
  const [reNurtureTheme, setReNurtureTheme] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [showEdit, setShowEdit] = useState(false);
  // Configured-pipeline movement info: { configured, current, targets } or null while loading
  const [moves, setMoves] = useState(null);

  const fetch = async () => {
    try {
      const r = await getLead(leadId);
      setLead(r.data);
      const m = await getLeadMoves(leadId);
      setMoves(m.data);
    } catch(e) {}
  };

  useEffect(() => { fetch(); }, [leadId]);
  useEffect(() => {
    if (isSuperAdmin()) {
      getUsers().then(r => setUsers(r.data.filter(u => u.isActive))).catch(() => {});
    }
  }, []);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 2500); };

  const moveStage = async (stage) => {
    setSaving(true);
    try {
      await movLeadStage(leadId, { stage, note: stageNote, lostReason, reNurtureDate: reNurtureDate || undefined, reNurtureContentTheme: reNurtureTheme });
      await fetch(); flash('Stage updated'); setStageNote(''); setLostReason(''); setReNurtureDate(''); setReNurtureTheme('');
    } catch(e) { flash(e.response?.data?.message || 'Cannot move to that stage'); }
    setSaving(false);
  };

  const moveToNode = async (targetNodeId) => {
    setSaving(true);
    try {
      await moveLeadToNode(leadId, { targetNodeId, note: stageNote });
      await fetch(); flash('Stage updated'); setStageNote('');
    } catch(e) { flash(e.response?.data?.message || 'Cannot move to that stage'); }
    setSaving(false);
  };

  const logActivity = async () => {
    if (!activityNote) return;
    setSaving(true);
    try { await addLeadActivity(leadId, { action: activityAction, note: activityNote }); await fetch(); setActivityNote(''); flash('Activity logged'); } catch(e) {}
    setSaving(false);
  };

  const reassign = async () => {
    if (!selectedAssignee) return;
    const u = users.find(u => u._id === selectedAssignee);
    if (!u) return;
    setSaving(true);
    try {
      await assignLead(leadId, { assignedTo: u._id, assignedToName: u.name });
      await fetch(); setSelectedAssignee(''); flash('Lead reassigned');
    } catch(e) { flash(e.response?.data?.message || 'Failed to reassign'); }
    setSaving(false);
  };

  const saveFollowUp = async () => {
    if (!followUpDate) return;
    setSaving(true);
    try { await setLeadFollowUp(leadId, { date: followUpDate, note: followUpNote }); await fetch(); setFollowUpDate(''); setFollowUpNote(''); flash('Follow-up set'); } catch(e) {}
    setSaving(false);
  };

  if (!lead) return (
    <div style={s.overlay}><div style={s.modal}><div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Loading...</div></div></div>
  );

  const currentStageIndex = STAGE_FLOW.findIndex(st => st.key === lead.stage);

  return (
    <div style={s.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={s.modal}>
        {/* Header */}
        <div style={s.header}>
          <div>
            <div style={s.leadName}>
              {lead.source === 'upwork' && <UpworkBadge size={15} style={{ marginRight: 6 }} />}
              {lead.name}
            </div>
            <div style={s.leadCompany}>{lead.company} · {SOURCE_LABELS[lead.source] || lead.source}</div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button style={s.editBtn} onClick={() => setShowEdit(true)}>Edit</button>
            <button style={s.close} onClick={onClose}>×</button>
          </div>
        </div>

        {/* Stage indicator — configured pipeline shows the current node (the graph
            isn't linear, so a progress bar would be misleading); legacy shows the
            old fixed progress bar */}
        {moves?.configured ? (
          <div style={s.stageBar}>
            {lead.pipelineNodeId && moves.current && lead.pipelineNodeId !== moves.current.nodeId ? (
              <>
                <span style={{ ...s.currentNodeChip, fontStyle: 'italic', color: '#999', background: '#ECEAE4' }}>
                  {lead.pipelineNodeIcon} {lead.pipelineNodeLabel || 'Undefined stage'}
                </span>
                <span style={{ fontSize: 11, color: '#A32D2D' }}>This stage was removed from the pipeline</span>
              </>
            ) : (
              <>
                <span style={s.currentNodeChip}>{moves.current?.icon} {moves.current?.label}</span>
                <span style={{ fontSize: 11, color: '#aaa' }}>Current pipeline stage</span>
              </>
            )}
          </div>
        ) : (
          <div style={s.stageBar}>
            {STAGE_FLOW.map((st, i) => (
              <div key={st.key} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <div style={{ ...s.stageDot, background: i <= currentStageIndex ? st.color : '#e0e0e0', border: lead.stage === st.key ? `2px solid ${st.color}` : '2px solid transparent' }} />
                <span style={{ fontSize: 9, color: i <= currentStageIndex ? st.color : '#aaa', textAlign: 'center', fontWeight: lead.stage === st.key ? 600 : 400 }}>{st.label}</span>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div style={s.tabs}>
          {['overview', 'move stage', 'activity', 'follow-up'].map(t => (
            <button key={t} style={{ ...s.tab, ...(tab === t ? s.tabActive : {}) }} onClick={() => setTab(t)}>{t}</button>
          ))}
        </div>

        <div style={s.body}>
          {msg && <div style={s.flash}>{msg}</div>}

          {/* Overview tab */}
          {tab === 'overview' && (
            <div>
              <Grid>
                <Info label="Email" value={lead.email || '—'} />
                <Info label="Phone" value={lead.phone || '—'} />
                <Info label="AI use case" value={USE_CASE_LABELS[lead.aiUseCase] || '—'} />
                <Info label="Deal size" value={DEAL_LABELS[lead.dealSize] || '—'} />
                <Info label="Budget estimate" value={lead.budgetEstimate ? `$${Number(lead.budgetEstimate).toLocaleString()}` : '—'} />
                <Info label="Timeline" value={TIMELINE_LABELS[lead.timeline] || '—'} />
                <Info label="Assigned to" value={lead.assignedToName || 'Unassigned'} />
                <Info label="Added by" value={lead.createdByName || '—'} />
              </Grid>
              {lead.linkedinUrl && <div style={{ marginTop: 12 }}><a href={lead.linkedinUrl} target="_blank" rel="noreferrer" style={{ color: '#534AB7', fontSize: 13 }}>View LinkedIn profile →</a></div>}
              {lead.upworkJobUrl && <div style={{ marginTop: 6 }}><a href={lead.upworkJobUrl} target="_blank" rel="noreferrer" style={{ color: '#14a800', fontSize: 13 }}>View Upwork job →</a></div>}
              {lead.upworkProposalId && (
                <div style={{ marginTop: 14, padding: 12, background: '#f6f9f6', border: '1px solid #d7ecd7', borderRadius: 10 }}>
                  <strong style={{ fontSize: 11, color: '#14a800', textTransform: 'uppercase' }}>Submitted proposal</strong>
                  <Grid>
                    <Info label="Job title" value={lead.upworkJobTitle || '—'} />
                    <Info label="Budget" value={lead.upworkBudget || '—'} />
                    <Info label="Contract type" value={lead.upworkContractType || '—'} />
                    <Info label="Status" value={lead.upworkProposalStatus || '—'} />
                    <Info label="Viewed by client" value={lead.upworkViewedByClient ? 'Yes' : 'No'} />
                    <Info label="Client rating" value={lead.upworkClientRating != null ? Number(lead.upworkClientRating).toFixed(1) : '—'} />
                  </Grid>
                  {!lead.upworkRoomId && (
                    <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>No client response yet — name/company will fill in automatically once they reply.</div>
                  )}
                </div>
              )}
              {lead.notes && <div style={s.notes}><strong style={{ fontSize: 11, color: '#888', textTransform: 'uppercase' }}>Notes</strong><p style={{ fontSize: 13, marginTop: 6, color: '#444', lineHeight: 1.6 }}>{lead.notes}</p></div>}
              {lead.nextFollowUpDate && (
                <div style={s.followUpBox}>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>⏰ Follow-up: {new Date(lead.nextFollowUpDate).toLocaleDateString()}</span>
                  {lead.nextFollowUpNote && <span style={{ fontSize: 12, color: '#666', marginTop: 2, display: 'block' }}>{lead.nextFollowUpNote}</span>}
                </div>
              )}
              {lead.stage === 're_nurture' && (
                <div style={{ ...s.followUpBox, background: '#EEEDFE', borderColor: '#534AB7' }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: '#534AB7' }}>♻ Re-nurture track</span>
                  {lead.reNurtureDate && <div style={{ fontSize: 12, color: '#534AB7', marginTop: 2 }}>Re-contact: {new Date(lead.reNurtureDate).toLocaleDateString()}</div>}
                  {lead.reNurtureContentTheme && <div style={{ fontSize: 12, color: '#534AB7' }}>Content theme: {lead.reNurtureContentTheme}</div>}
                  {lead.lostReason && <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>Lost reason: {lead.lostReason}</div>}
                </div>
              )}

              {isSuperAdmin() && users.length > 0 && (
                <div style={{ marginTop: 16, padding: '12px', background: '#fafafa', borderRadius: 8, border: '1px solid #f0efe9' }}>
                  <div style={s.fieldLabel}>Reassign lead</div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                    <select style={{ ...s.input, flex: 1 }} value={selectedAssignee} onChange={e => setSelectedAssignee(e.target.value)}>
                      <option value="">— Select team member —</option>
                      {users.map(u => (
                        <option key={u._id} value={u._id} disabled={u._id === (lead.assignedTo?._id || lead.assignedTo)}>
                          {u.name}{u._id === (lead.assignedTo?._id || lead.assignedTo) ? ' (current)' : ''}
                        </option>
                      ))}
                    </select>
                    <button style={s.primaryBtn} onClick={reassign} disabled={saving || !selectedAssignee}>
                      {saving ? 'Saving...' : 'Reassign'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Move stage tab — configured pipeline: only stages connected from the current node */}
          {tab === 'move stage' && moves?.configured && (
            <div>
              {lead.pipelineNodeId && moves.current && lead.pipelineNodeId !== moves.current.nodeId ? (
                <p style={{ fontSize: 13, color: '#666', marginBottom: 14 }}>
                  Last known stage: <strong style={{ fontStyle: 'italic', color: '#999' }}>{lead.pipelineNodeIcon} {lead.pipelineNodeLabel || 'Undefined stage'}</strong>
                  {' '}— that stage no longer exists in the pipeline, so available moves below start from the entry stage.
                </p>
              ) : (
                <p style={{ fontSize: 13, color: '#666', marginBottom: 14 }}>
                  Current stage: <strong style={{ color: '#534AB7' }}>{moves.current?.icon} {moves.current?.label}</strong>
                </p>
              )}
              {moves.targets.length === 0 ? (
                <div style={{ fontSize: 13, color: '#aaa', padding: '16px 0' }}>
                  No stages are connected from this stage in the pipeline. Add connections in the Pipeline Builder to enable movement.
                </div>
              ) : (
                <>
                  <label style={s.fieldLabel}>Note for this stage move</label>
                  <textarea style={s.textarea} value={stageNote} onChange={e => setStageNote(e.target.value)} placeholder="What happened? Why are you moving this lead?" />
                  <div style={{ ...s.fieldLabel, marginTop: 12 }}>Move to</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {moves.targets.map(t => (
                      <button key={t.nodeId} style={{ ...s.stageBtn, borderColor: '#534AB7', color: '#534AB7' }} onClick={() => moveToNode(t.nodeId)} disabled={saving}>
                        {t.icon} {t.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Move stage tab — legacy hardcoded transitions (no pipeline configured) */}
          {tab === 'move stage' && moves && !moves.configured && (() => {
            const allowed = STAGE_TRANSITIONS[lead.stage] || [];
            const needsLostReason = allowed.includes('closed_lost');
            const needsReNurture = allowed.includes('re_nurture');
            return (
              <div>
                <p style={{ fontSize: 13, color: '#666', marginBottom: 14 }}>
                  Current stage: <strong style={{ color: ALL_STAGES[lead.stage]?.color }}>{ALL_STAGES[lead.stage]?.label || lead.stage}</strong>
                </p>

                {allowed.length === 0 ? (
                  <div style={{ fontSize: 13, color: '#aaa', padding: '16px 0' }}>No further transitions available from this stage.</div>
                ) : (
                  <>
                    <label style={s.fieldLabel}>Note for this stage move</label>
                    <textarea style={s.textarea} value={stageNote} onChange={e => setStageNote(e.target.value)} placeholder="What happened? Why are you moving this lead?" />

                    {needsLostReason && (
                      <div style={{ marginBottom: 10 }}>
                        <label style={s.fieldLabel}>Lost reason <span style={{ textTransform: 'none', fontWeight: 400 }}>(required when closing lost)</span></label>
                        <input style={s.input} value={lostReason} onChange={e => setLostReason(e.target.value)} placeholder="Budget, timing, competitor, etc." />
                      </div>
                    )}

                    {needsReNurture && (
                      <>
                        <div style={{ marginBottom: 10 }}>
                          <label style={s.fieldLabel}>Re-nurture date <span style={{ textTransform: 'none', fontWeight: 400 }}>(if moving to re-nurture)</span></label>
                          <input type="date" style={s.input} value={reNurtureDate} onChange={e => setReNurtureDate(e.target.value)} />
                        </div>
                        <div style={{ marginBottom: 14 }}>
                          <label style={s.fieldLabel}>Re-nurture content theme</label>
                          <input style={s.input} value={reNurtureTheme} onChange={e => setReNurtureTheme(e.target.value)} placeholder="e.g. Agentic AI ROI case studies" />
                        </div>
                      </>
                    )}

                    <div style={s.fieldLabel}>Move to</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {allowed.map(key => {
                        const st = ALL_STAGES[key];
                        return (
                          <button key={key} style={{ ...s.stageBtn, borderColor: st.color, color: st.color }} onClick={() => moveStage(key)} disabled={saving}>
                            {st.label}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            );
          })()}

          {/* Activity tab */}
          {tab === 'activity' && (
            <div>
              <div style={{ marginBottom: 14 }}>
                <label style={s.fieldLabel}>Log an activity</label>
                <select style={s.input} value={activityAction} onChange={e => setActivityAction(e.target.value)}>
                  {['Call made', 'Email sent', 'Meeting held', 'Demo given', 'Proposal discussed', 'Follow-up sent', 'LinkedIn message', 'WhatsApp message', 'Note added'].map(a => <option key={a}>{a}</option>)}
                </select>
                <textarea style={{ ...s.textarea, marginTop: 8 }} value={activityNote} onChange={e => setActivityNote(e.target.value)} placeholder="What happened in this interaction?" />
                <button style={s.primaryBtn} onClick={logActivity} disabled={saving || !activityNote}>{saving ? 'Saving...' : 'Log activity'}</button>
              </div>
              <div style={s.fieldLabel}>Activity history</div>
              {lead.activityLog?.length === 0 ? <div style={{ color: '#aaa', fontSize: 13 }}>No activity logged yet.</div> :
                [...(lead.activityLog || [])].reverse().map((a, i) => (
                  <div key={i} style={s.activityItem}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: '#1a1a1a' }}>{a.action}</div>
                    <div style={{ fontSize: 12, color: '#555', marginTop: 2, lineHeight: 1.5 }}>{a.note}</div>
                    <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>{a.performedByName || 'Team'} · {a.createdAt ? new Date(a.createdAt).toLocaleString() : ''}</div>
                  </div>
                ))
              }
            </div>
          )}

          {/* Follow-up tab */}
          {tab === 'follow-up' && (
            <div>
              <label style={s.fieldLabel}>Schedule a follow-up</label>
              <input type="date" style={s.input} value={followUpDate} onChange={e => setFollowUpDate(e.target.value)} />
              <textarea style={{ ...s.textarea, marginTop: 8 }} value={followUpNote} onChange={e => setFollowUpNote(e.target.value)} placeholder="What do you want to follow up about?" />
              <button style={s.primaryBtn} onClick={saveFollowUp} disabled={saving || !followUpDate}>{saving ? 'Saving...' : 'Set follow-up'}</button>
            </div>
          )}
        </div>
      </div>
      {showEdit && (
        <LeadForm
          initialLead={lead}
          onClose={() => setShowEdit(false)}
          onSaved={() => { setShowEdit(false); fetch(); flash('Lead updated'); }}
        />
      )}
    </div>
  );
}

function Grid({ children }) { return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>{children}</div>; }
function Info({ label, value }) {
  return (
    <div style={{ padding: '8px 0', borderBottom: '1px solid #f5f4f0' }}>
      <div style={{ fontSize: 11, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, color: '#1a1a1a', fontWeight: 500 }}>{value}</div>
    </div>
  );
}

const s = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 },
  modal: { background: '#fff', borderRadius: 16, width: '100%', maxWidth: 580, maxHeight: '92vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #f0efe9' },
  leadName: { fontSize: 17, fontWeight: 600, color: '#1a1a1a' },
  leadCompany: { fontSize: 13, color: '#888', marginTop: 2 },
  editBtn: { padding: '5px 14px', borderRadius: 8, border: '1px solid #534AB7', background: '#fff', color: '#534AB7', fontSize: 12, fontWeight: 500, cursor: 'pointer' },
  close: { background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#888', lineHeight: 1, padding: 0 },
  stageBar: { display: 'flex', padding: '12px 16px', borderBottom: '1px solid #f0efe9', gap: 4, alignItems: 'center' },
  currentNodeChip: { fontSize: 13, fontWeight: 600, color: '#534AB7', background: '#EEEDFE', borderRadius: 8, padding: '5px 12px' },
  stageDot: { width: 10, height: 10, borderRadius: '50%', transition: 'background .2s' },
  tabs: { display: 'flex', borderBottom: '1px solid #f0efe9', padding: '0 16px' },
  tab: { padding: '10px 12px', fontSize: 12, fontWeight: 500, border: 'none', background: 'none', cursor: 'pointer', color: '#888', borderBottom: '2px solid transparent', textTransform: 'capitalize' },
  tabActive: { color: '#534AB7', borderBottomColor: '#534AB7' },
  body: { padding: '16px 20px', overflowY: 'auto', flex: 1 },
  flash: { background: '#E1F5EE', color: '#0F6E56', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12, fontWeight: 500 },
  notes: { marginTop: 16, padding: '12px', background: '#fafafa', borderRadius: 8, border: '1px solid #f0efe9' },
  followUpBox: { marginTop: 12, padding: '10px 12px', background: '#FAEEDA', borderRadius: 8, border: '1px solid #FAC775' },
  fieldLabel: { fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 6 },
  input: { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #e0e0e0', fontSize: 13, boxSizing: 'border-box', outline: 'none', color: '#1a1a1a', background: '#fafafa' },
  textarea: { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #e0e0e0', fontSize: 13, boxSizing: 'border-box', outline: 'none', minHeight: 80, resize: 'vertical', color: '#1a1a1a', background: '#fafafa' },
  primaryBtn: { marginTop: 10, padding: '9px 18px', background: '#534AB7', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  stageBtn: { padding: '7px 14px', borderRadius: 8, border: '1.5px solid', background: '#fff', fontSize: 12, fontWeight: 500, cursor: 'pointer' },
  activityItem: { padding: '10px 0', borderBottom: '1px solid #f5f4f0' },
};
